/**
 * GuidedTour — a lightweight, dependency-free "?tour=1" overlay for
 * contest judges (or any first-time visitor). Walks them through the
 * key SociBaba™ surfaces one step at a time with a clear caption,
 * "Skip tour", "← Back", "Next →" and a "Finish" state.
 *
 * Design choices:
 *  · Zero external tour library (no react-joyride) so the bundle stays
 *    slim and there's no clash with our existing Framer Motion / Radix
 *    z-index layers.
 *  · Steps navigate the browser via react-router — the overlay stays
 *    mounted at the app root, so each step transitions the URL and
 *    then re-highlights a `data-testid` (or a viewport centred card
 *    when no element is present on the page).
 *  · Persisted in `sessionStorage` so refreshes inside the tour keep
 *    the state, but a new tab starts fresh.
 *  · Ignored unless the URL contains `?tour=1` on first landing.
 *    Removing the param exits the tour cleanly.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowLeft, ArrowRight, Sparkles, Check } from "lucide-react";

interface TourStep {
  id: string;
  title: string;
  body: string;
  route: string;              // where the tour should live for this step
  targetTid?: string;         // data-testid to highlight (optional)
  ctaLabel?: string;          // custom CTA for the final step
}

const STEPS: TourStep[] = [
  {
    id: "welcome",
    title: "Welcome to SociBaba™",
    body:
      "The AI Product Intelligence Platform. In ~2 minutes we’ll walk you through the four surfaces that make it a category of one: SibaScore™, AI Cart™ Search, Sancta and the Seller Workspace.",
    route: "/",
    targetTid: "hero-headline",
  },
  {
    id: "reel",
    title: "Watch how it works",
    body:
      "Four synthetic reels — SibaScore™ (the trust engine), Shoppers, Creators, and Brands & Sellers. Real product primitives, not marketing fluff. Tap any tab.",
    route: "/",
    targetTid: "landing-explainer-reel-section",
  },
  {
    id: "sibascore",
    title: "SibaScore™ — the trust engine",
    body:
      "Every product carries a single honest number out of 1000, composed live from eleven signals across reviews, videos, Reddit, marketplaces and creator content.",
    route: "/shop",
    targetTid: "mode-switcher-shop",
  },
  {
    id: "ai-cart",
    title: "AI Cart™ Search",
    body:
      "Paste any product URL. AI Cart™ finds the same item across marketplaces, matches you with the verified seller, applies your VIP loyalty tier and closes the deal in seconds.",
    route: "/ai-cart",
    targetTid: "mode-switcher-ai-cart",
  },
  {
    id: "sancta",
    title: "Sancta — the A→Z assistant",
    body:
      "The multilingual chatbot woven into every surface. Speaks 12 languages, drafts grounded replies for creators, surfaces insights for sellers and answers questions for shoppers.",
    route: "/",
    targetTid: "landing-page",
  },
  {
    id: "seller",
    title: "Seller Workspace",
    body:
      "AutoPilot, Intent Radar, Sancta Insights, Cheer Wall, AI Ad Optimizer and Money Meter — one dashboard, twelve languages, six currencies. Ready to earn.",
    route: "/pricing",
    ctaLabel: "Finish tour",
  },
];

const STORAGE_KEY = "sb_tour_step";

export const GuidedTour: React.FC = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  // Enter the tour when `?tour=1` is present, or resume from
  // sessionStorage if the browser reloaded mid-tour.
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const paramWantsTour = params.get("tour") === "1";
  const [active, setActive] = useState<boolean>(() => {
    if (paramWantsTour) return true;
    try {
      return sessionStorage.getItem(STORAGE_KEY) !== null;
    } catch {
      return false;
    }
  });
  const [stepIdx, setStepIdx] = useState<number>(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      const n = raw ? parseInt(raw, 10) : 0;
      return Number.isFinite(n) && n >= 0 && n < STEPS.length ? n : 0;
    } catch {
      return 0;
    }
  });
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const rafRef = useRef<number | null>(null);
  const step = STEPS[stepIdx];

  // Kick off when `?tour=1` first appears.
  useEffect(() => {
    if (!paramWantsTour) return;
    setActive(true);
    setStepIdx(0);
    try { sessionStorage.setItem(STORAGE_KEY, "0"); } catch { /* ignore */ }
    // Strip the query param so refreshes don’t re-trigger step 0.
    const next = new URLSearchParams(search);
    next.delete("tour");
    navigate(
      { pathname: STEPS[0].route, search: next.toString() ? `?${next.toString()}` : "" },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramWantsTour]);

  // Navigate to the current step’s route when it changes.
  useEffect(() => {
    if (!active) return;
    if (!step) return;
    if (pathname !== step.route) {
      navigate(step.route, { replace: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, stepIdx]);

  // Track the target element’s rect so the spotlight follows layout shifts.
  useEffect(() => {
    if (!active || !step?.targetTid) {
      setTargetRect(null);
      return;
    }
    const measure = () => {
      const el = document.querySelector<HTMLElement>(
        `[data-testid="${step.targetTid}"]`,
      );
      if (!el) {
        setTargetRect(null);
      } else {
        setTargetRect(el.getBoundingClientRect());
        try {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch {
          el.scrollIntoView();
        }
      }
    };
    // Slight delay so route transitions have time to render targets.
    const t = window.setTimeout(measure, 350);
    const loop = () => {
      measure();
      rafRef.current = window.requestAnimationFrame(loop);
    };
    rafRef.current = window.requestAnimationFrame(loop);
    return () => {
      window.clearTimeout(t);
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    };
  }, [active, stepIdx, step, pathname]);

  const persist = useCallback((n: number | null) => {
    try {
      if (n === null) sessionStorage.removeItem(STORAGE_KEY);
      else sessionStorage.setItem(STORAGE_KEY, String(n));
    } catch { /* ignore */ }
  }, []);

  const next = useCallback(() => {
    if (stepIdx >= STEPS.length - 1) {
      setActive(false);
      persist(null);
      return;
    }
    const n = stepIdx + 1;
    setStepIdx(n);
    persist(n);
  }, [stepIdx, persist]);

  const back = useCallback(() => {
    if (stepIdx <= 0) return;
    const n = stepIdx - 1;
    setStepIdx(n);
    persist(n);
  }, [stepIdx, persist]);

  const skip = useCallback(() => {
    setActive(false);
    persist(null);
  }, [persist]);

  // Keyboard shortcuts (Esc = skip, ← = back, → = next)
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") skip();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") back();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, next, back, skip]);

  if (!active || !step || typeof document === "undefined") return null;

  const isLast = stepIdx === STEPS.length - 1;

  // Spotlight math — pad the target rect by 12px, fall back to a
  // centered card if no target on this route.
  const pad = 12;
  const spotlight = targetRect
    ? {
        top:   targetRect.top - pad,
        left:  targetRect.left - pad,
        width: targetRect.width + pad * 2,
        height: targetRect.height + pad * 2,
      }
    : null;

  // Card position: below spotlight when possible, else viewport-centred.
  const cardWidth = 380;
  const viewportW = typeof window !== "undefined" ? window.innerWidth : 1024;
  const viewportH = typeof window !== "undefined" ? window.innerHeight : 768;
  let cardTop = 0;
  let cardLeft = 0;
  if (spotlight) {
    cardTop = spotlight.top + spotlight.height + 16;
    cardLeft = spotlight.left + spotlight.width / 2 - cardWidth / 2;
    // Flip above if it overflows the viewport
    if (cardTop + 260 > viewportH) cardTop = Math.max(16, spotlight.top - 260 - 16);
    cardLeft = Math.max(16, Math.min(cardLeft, viewportW - cardWidth - 16));
  } else {
    cardTop = Math.max(16, viewportH / 2 - 130);
    cardLeft = Math.max(16, viewportW / 2 - cardWidth / 2);
  }

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="tour-root"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[9999] pointer-events-none"
        data-testid="guided-tour-root"
      >
        {/* Full-viewport dim + optional spotlight cutout via SVG mask */}
        <svg className="absolute inset-0 h-full w-full pointer-events-auto" data-testid="guided-tour-scrim">
          <defs>
            <mask id="tour-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {spotlight && (
                <rect
                  x={spotlight.left}
                  y={spotlight.top}
                  width={spotlight.width}
                  height={spotlight.height}
                  rx="16"
                  ry="16"
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(15, 23, 42, 0.7)"
            mask="url(#tour-mask)"
          />
        </svg>

        {/* Spotlight border ring */}
        {spotlight && (
          <div
            className="absolute rounded-2xl border-2 border-fuchsia-400/90 shadow-[0_0_0_4px_rgba(217,70,239,0.25)] pointer-events-none"
            style={{
              top: spotlight.top,
              left: spotlight.left,
              width: spotlight.width,
              height: spotlight.height,
            }}
            data-testid="guided-tour-spotlight"
          />
        )}

        {/* Step card */}
        <motion.div
          key={step.id}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          transition={{ duration: 0.25 }}
          className="absolute rounded-2xl bg-white shadow-2xl border border-slate-200 pointer-events-auto"
          style={{ top: cardTop, left: cardLeft, width: cardWidth }}
          data-testid="guided-tour-card"
          role="dialog"
          aria-label={`Guided tour · ${step.title}`}
        >
          <div className="p-5 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-fuchsia-600 font-semibold">
                <Sparkles className="h-3 w-3" />
                Guided demo · {stepIdx + 1} of {STEPS.length}
              </div>
              <button
                type="button"
                onClick={skip}
                aria-label="Skip tour"
                data-testid="guided-tour-close"
                className="h-6 w-6 rounded-full inline-flex items-center justify-center text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="font-display font-bold text-lg text-slate-900 tracking-tight" data-testid="guided-tour-title">
              {step.title}
            </div>
            <div className="text-sm text-slate-600 leading-relaxed" data-testid="guided-tour-body">
              {step.body}
            </div>
            {/* Progress dots */}
            <div className="flex items-center gap-1.5" data-testid="guided-tour-progress">
              {STEPS.map((s, i) => (
                <span
                  key={s.id}
                  className={`h-1.5 rounded-full transition-all ${
                    i === stepIdx ? "w-6 bg-fuchsia-500" : i < stepIdx ? "w-1.5 bg-fuchsia-300" : "w-1.5 bg-slate-200"
                  }`}
                />
              ))}
            </div>
            {/* Controls */}
            <div className="flex items-center justify-between gap-2 pt-1">
              <button
                type="button"
                onClick={skip}
                data-testid="guided-tour-skip"
                className="text-xs text-slate-500 hover:text-slate-900 transition-colors font-semibold"
              >
                Skip tour
              </button>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={back}
                  disabled={stepIdx === 0}
                  data-testid="guided-tour-back"
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 text-slate-700 text-xs font-semibold px-3 py-1.5 hover:border-slate-900 hover:text-slate-900 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> Back
                </button>
                <button
                  type="button"
                  onClick={next}
                  data-testid="guided-tour-next"
                  className="inline-flex items-center gap-1 rounded-full bg-slate-900 hover:bg-black text-white text-xs font-semibold px-3.5 py-1.5 transition-colors"
                >
                  {isLast ? (
                    <>
                      <Check className="h-3.5 w-3.5" /> {step.ctaLabel || "Finish"}
                    </>
                  ) : (
                    <>
                      Next <ArrowRight className="h-3.5 w-3.5" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
};

export default GuidedTour;
